import React, { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import PropTypes from "prop-types";
import { StyledTextField } from "components/form/form-textfield/form-textfield";
import { injectIntl } from "react-intl";
import { DataGridItem } from "components/data-grid";
import { SimpleCheckbox } from "components/form/form-checkbox";
import Typography from "@material-ui/core/Typography";
import { withStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import styles from "../../styles/room-template-form.css";
import defaultValues from "./default-values";
import {
  AdvancedSettingsSection,
  FileConfigurationSection,
  MixerSection,
  RecordingsSection,
  ScoreboardSection,
  StreamSourcesSection,
  VirtualCamSection,
} from "./partials";

const NewRoomTemplateForm = ({
  intl,
  setValue,
  values,
  errors,
  validationErrors,
  allStreamSources = [],
  setAllStreamSources,
  selectedStreamSources: selectedStreamSourcesProp,
  setSelectedStreamSources: setSelectedStreamSourcesProp,
  selectedRecordings: selectedRecordingsProp,
  setSelectedRecordings: setSelectedRecordingsProp,
  expandedAccordions: expandedAccordionsProp,
  setExpandedAccordions: setExpandedAccordionsProp,
  renderBasicFieldsOnly = false,
  renderSection = null,
  inputMode: inputModeProp,
  setInputMode: setInputModeProp,
  skipAutoInitialization,
  initialAudioLinks = null,
  onFileUploaded,
  classes,
}) => {
  const [inputModeLocal, setInputModeLocal] = useState(null);
  const inputMode = inputModeProp !== undefined ? inputModeProp : inputModeLocal;
  const setInputMode = setInputModeProp || setInputModeLocal;
  const [selectedStreamSourcesLocal, setSelectedStreamSourcesLocal] = useState([]);
  const selectedStreamSources =
    selectedStreamSourcesProp !== undefined ? selectedStreamSourcesProp : selectedStreamSourcesLocal;
  const setSelectedStreamSources = setSelectedStreamSourcesProp || setSelectedStreamSourcesLocal;
  const [selectedRecordingsLocal, setSelectedRecordingsLocal] = useState([]);
  const selectedRecordings = selectedRecordingsProp !== undefined ? selectedRecordingsProp : selectedRecordingsLocal;
  const setSelectedRecordings = setSelectedRecordingsProp || setSelectedRecordingsLocal;
  const [expandedAccordionsLocal, setExpandedAccordionsLocal] = useState({});
  const expandedAccordions = expandedAccordionsProp !== undefined ? expandedAccordionsProp : expandedAccordionsLocal;
  const setExpandedAccordions = setExpandedAccordionsProp || setExpandedAccordionsLocal;
  const [camOpSourceIndex, setCamOpSourceIndex] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [audioSourceLinks, setAudioSourceLinks] = useState(new Map());
  const [sourceFormData, setSourceFormData] = useState({});

  useEffect(
    () => {
      if (initialAudioLinks) {
        setAudioSourceLinks(initialAudioLinks);
      }
    },
    [initialAudioLinks],
  );

  useEffect(
    () => {
      if (skipAutoInitialization) {
        return;
      }

      if (allStreamSources.length > 0 && !values.file?.video_drains?.recordings) {
        const programRecording = {
          src: "program",
        };

        setValue("file.video_drains.recordings", [programRecording]);

        if (!values.file?.stream_sources) {
          setValue("file.stream_sources", []);
        }

        if (!values.file?.mixer?.video) {
          setValue("file.mixer.video", []);
        }

        if (!values.file?.mixer?.audio) {
          setValue("file.mixer.audio", []);
        }

        if (values.file?.mixer?.auto === undefined) {
          setValue("file.mixer.auto", false);
        }

        if (!values.file?.mixer?.score) {
          setValue("file.mixer.score", {
            default: "drawn",
            titler_url: "",
            use_titler: false,
            drawn: {
              auto: false,
              cfg_src: "",
              params: "",
            },
            burnt: {
              score_cam: "",
              score_perspective: "",
              score_scale: "",
              margin: "",
              place: "TR",
            },
          });
        }
      }
    },
    [allStreamSources],
  );

  useEffect(
    () => {
      const formSources = values.file?.stream_sources;
      if (!formSources || formSources.length === 0) {
        return;
      }

      const panSourceIndex = formSources.findIndex(source => source.label === "PAN" && source.steerable);

      if (panSourceIndex !== -1) {
        const allSourceIndex = selectedStreamSources[panSourceIndex];

        if (allSourceIndex !== undefined && camOpSourceIndex !== allSourceIndex) {
          setCamOpSourceIndex(allSourceIndex);
        }
      } else if (camOpSourceIndex !== null) {
        setCamOpSourceIndex(null);
      }
    },
    [values.file?.stream_sources, selectedStreamSources],
  );

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setValue(name, type === "checkbox" ? checked : value);
  };

  const handleAddVptzSource = vptzSource => {
    const vptzIndex = allStreamSources.length;

    flushSync(() => {
      setAllStreamSources(prevAllStreamSources => {
        return [...prevAllStreamSources, vptzSource];
      });

      setSelectedStreamSources(prevSelected => {
        return [...prevSelected, vptzIndex];
      });

      // Keep the recordings selection in sync so the recording's checkbox appears marked in UI
      setSelectedRecordings(prevSelected => {
        if (prevSelected.includes(vptzIndex)) return prevSelected;
        return [...prevSelected, vptzIndex].sort((a, b) => a - b);
      });

      // Expand the accordion synchronously so it's available immediately
      setExpandedAccordions(prev => {
        return { ...prev, [vptzIndex]: true };
      });
    });

    const { description, ...sourceWithoutDescription } = vptzSource;
    const { baseUrl, params } = parseUrlParameters(vptzSource.url);
    const hasDewarpParam = params.some(p => p.key === "dewarp_id");
    let vptzUrl = vptzSource.url;
    if (!hasDewarpParam) {
      const newParams = [...params, { key: "dewarp_id", value: "" }];
      vptzUrl = buildUrlWithParameters(baseUrl, newParams);
    }

    const newFormSource = {
      ...sourceWithoutDescription,
      url: vptzUrl,
      cam_id: vptzSource.label,
    };

    const currentFormSources = values.file?.stream_sources || [];
    setValue("file.stream_sources", [...currentFormSources, newFormSource]);

    const currentVideoLabels = values.file?.mixer?.video || [];
    if (!currentVideoLabels.includes(vptzSource.label)) {
      setValue("file.mixer.video", [...currentVideoLabels, vptzSource.label]);
    }

    const currentRecordings = values.file?.video_drains?.recordings || [];
    const vptzRecordingExists = currentRecordings.some(rec => rec.src === vptzSource.label);
    if (!vptzRecordingExists) {
      const vptzRecording = { src: vptzSource.label };
      const programIndex = currentRecordings.findIndex(rec => rec.src === "program");
      if (programIndex !== -1) {
        const newRecordings = [...currentRecordings];
        newRecordings.splice(programIndex, 0, vptzRecording);
        setValue("file.video_drains.recordings", newRecordings);
      } else {
        setValue("file.video_drains.recordings", [...currentRecordings, vptzRecording]);
      }
    }
  };

  const parseUrlParameters = url => {
    if (!url) return { baseUrl: "", params: [] };

    try {
      const questionMarkIndex = url.indexOf("?");
      if (questionMarkIndex === -1) {
        return { baseUrl: url, params: [] };
      }

      const baseUrl = url.substring(0, questionMarkIndex);
      const queryString = url.substring(questionMarkIndex + 1);

      const params = [];
      const urlParams = new URLSearchParams(queryString);
      urlParams.forEach((value, key) => {
        params.push({ key, value });
      });

      return { baseUrl, params };
    } catch (e) {
      return { baseUrl: url, params: [] };
    }
  };

  const buildUrlWithParameters = (baseUrl, params) => {
    if (!params || params.length === 0) return baseUrl;
    const validParams = params.filter(p => p.key && p.key.trim() !== "");
    if (validParams.length === 0) return baseUrl;

    const urlParams = new URLSearchParams();
    validParams.forEach(param => {
      urlParams.append(param.key, param.value || "");
    });
    return `${baseUrl}?${urlParams.toString()}`;
  };

  const getUrlParameters = streamSourceIndex => {
    const selectedIndex = selectedStreamSources.indexOf(streamSourceIndex);
    const streamSource = allStreamSources[streamSourceIndex];

    if (selectedIndex === -1) {
      if (!streamSource?.url) return [];
      return parseUrlParameters(streamSource.url).params;
    }

    const formSource = values.file?.stream_sources?.[selectedIndex];
    if (!formSource) return [];

    if (formSource._tempParams) {
      return formSource._tempParams;
    }

    const url = formSource.url || streamSource?.url;
    if (!url) return [];
    return parseUrlParameters(url).params;
  };

  const handleAddUrlParameter = streamSourceIndex => () => {
    const selectedIndex = selectedStreamSources.indexOf(streamSourceIndex);
    if (selectedIndex === -1) return;

    const currentFormSources = values.file?.stream_sources || [];
    const formSource = currentFormSources[selectedIndex];
    const currentParams = formSource?._tempParams || parseUrlParameters(formSource?.url || "").params;

    const newParams = [...currentParams, { key: "", value: "" }];

    const updatedFormSources = [...currentFormSources];
    updatedFormSources[selectedIndex] = {
      ...updatedFormSources[selectedIndex],
      _tempParams: newParams,
    };
    setValue("file.stream_sources", updatedFormSources);
  };

  const handleRemoveUrlParameter = (streamSourceIndex, paramIndex) => () => {
    const selectedIndex = selectedStreamSources.indexOf(streamSourceIndex);
    if (selectedIndex === -1) return;

    const currentFormSources = values.file?.stream_sources || [];
    const formSource = currentFormSources[selectedIndex];
    const currentParams = formSource?._tempParams || parseUrlParameters(formSource?.url || "").params;

    const streamSource = allStreamSources[streamSourceIndex];
    if (streamSource?.brand === "Axis" && currentParams[paramIndex]?.key === "audio") {
      return;
    }

    if (streamSource?.label?.startsWith("vptz") && currentParams[paramIndex]?.key === "dewarp_id") {
      return;
    }

    const newParams = [...currentParams];
    newParams.splice(paramIndex, 1);

    const { baseUrl } = parseUrlParameters(formSource?.url || "");
    const newUrl = buildUrlWithParameters(baseUrl, newParams);
    const hasTempParams = newParams.some(p => !p.key || p.key.trim() === "");

    const updatedFormSources = [...currentFormSources];
    updatedFormSources[selectedIndex] = {
      ...updatedFormSources[selectedIndex],
      url: newUrl,
      _tempParams: hasTempParams ? newParams : undefined,
    };
    setValue("file.stream_sources", updatedFormSources);
  };

  const handleUrlParameterChange = (streamSourceIndex, paramIndex, field) => e => {
    const { value } = e.target;
    const selectedIndex = selectedStreamSources.indexOf(streamSourceIndex);
    if (selectedIndex === -1) return;

    const currentFormSources = values.file?.stream_sources || [];
    const formSource = currentFormSources[selectedIndex];
    const streamSource = allStreamSources[streamSourceIndex];
    const currentParams = formSource?._tempParams || parseUrlParameters(formSource?.url || "").params;

    const newParams = [...currentParams];
    newParams[paramIndex] = {
      ...newParams[paramIndex],
      [field]: value,
    };

    const { baseUrl } = parseUrlParameters(formSource?.url || streamSource?.url || "");
    const newUrl = buildUrlWithParameters(baseUrl, newParams);
    const hasTempParams = newParams.some(p => !p.key || p.key.trim() === "");

    const updatedFormSources = [...currentFormSources];
    updatedFormSources[selectedIndex] = {
      ...updatedFormSources[selectedIndex],
      url: newUrl,
      _tempParams: hasTempParams ? newParams : undefined,
    };

    setValue("file.stream_sources", updatedFormSources);
  };

  const handleStreamSourceTypeChange = streamSourceIndex => e => {
    const { value } = e.target;
    const selectedIndex = selectedStreamSources.indexOf(streamSourceIndex);
    if (selectedIndex === -1) return;

    setSourceFormData(prev => ({
      ...prev,
      [streamSourceIndex]: {
        ...prev[streamSourceIndex],
        type: value,
      },
    }));

    const currentFormSources = values.file?.stream_sources || [];
    const updatedFormSources = [...currentFormSources];
    updatedFormSources[selectedIndex] = {
      ...updatedFormSources[selectedIndex],
      type: value,
    };
    setValue("file.stream_sources", updatedFormSources);
  };

  const handleStreamSourceLabelChange = streamSourceIndex => e => {
    const { value } = e.target;
    const selectedIndex = selectedStreamSources.indexOf(streamSourceIndex);

    setSourceFormData(prev => ({
      ...prev,
      [streamSourceIndex]: {
        ...prev[streamSourceIndex],
        label: value,
      },
    }));

    if (selectedIndex === -1) return;

    const currentFormSources = values.file?.stream_sources || [];
    const oldLabel = currentFormSources[selectedIndex]?.label;

    // If label is changed to "PAN", activate Cam Op for this source
    if (value === "PAN" && camOpSourceIndex !== streamSourceIndex) {
      setCamOpSourceIndex(streamSourceIndex);

      // Update all sources to reflect the new Cam Op
      const updatedSources = currentFormSources.map((source, idx) => {
        if (idx === selectedIndex) {
          return {
            ...source,
            label: "PAN",
            camOp: true,
            steerable: {
              //exp_camera_url: "",
              method: "mechanical",
              auto: true,
              cfg: "",
              params: "-m",
            },
          };
        } else if (source.steerable) {
          // Remove steerable from other sources
          const { steerable, ...rest } = source;
          return { ...rest, camOp: false };
        }
        return { ...source, camOp: false };
      });
      setValue("file.stream_sources", updatedSources);

      // Remove PAN from preselected
      const currentPreselected = values.file?.mixer?.preselected || [];
      const updatedPreselected = currentPreselected.filter(label => label !== "PAN");
      setValue("file.mixer.preselected", updatedPreselected);

      setExpandedAccordions(prev => ({ ...prev, [streamSourceIndex]: true }));
    } else if (oldLabel === "PAN" && value !== "PAN" && camOpSourceIndex === streamSourceIndex) {
      // If changing away from "PAN", deactivate Cam Op
      setCamOpSourceIndex(null);

      const updatedFormSources = [...currentFormSources];
      updatedFormSources[selectedIndex] = {
        ...updatedFormSources[selectedIndex],
        label: value,
        camOp: false,
      };

      // Remove steerable if it exists
      if (updatedFormSources[selectedIndex].steerable) {
        delete updatedFormSources[selectedIndex].steerable;
      }

      setValue("file.stream_sources", updatedFormSources);
    } else {
      // Normal label change
      const updatedFormSources = [...currentFormSources];
      updatedFormSources[selectedIndex] = {
        ...updatedFormSources[selectedIndex],
        label: value,
      };
      setValue("file.stream_sources", updatedFormSources);
    }

    const currentAudioArray = values.file?.mixer?.audio || [];
    if (oldLabel && currentAudioArray.includes(oldLabel)) {
      const newAudioArray = currentAudioArray.map(audioLabel => (audioLabel === oldLabel ? value : audioLabel));
      setValue("file.mixer.audio", newAudioArray);
    }

    const currentPreselected = values.file?.mixer?.preselected || [];
    if (oldLabel && currentPreselected.includes(oldLabel)) {
      const newPreselected = currentPreselected.map(label => (label === oldLabel ? value : label));
      setValue("file.mixer.preselected", newPreselected);
    }

    const currentVideoArray = values.file?.mixer?.video || [];
    if (oldLabel && currentVideoArray.includes(oldLabel)) {
      const newVideoArray = currentVideoArray.map(videoLabel => (videoLabel === oldLabel ? value : videoLabel));
      setValue("file.mixer.video", newVideoArray);
    }

    const currentRecordings = values.file?.video_drains?.recordings || [];
    if (oldLabel) {
      const recordingIndex = currentRecordings.findIndex(rec => rec.src === oldLabel);
      if (recordingIndex !== -1) {
        const updatedRecordings = [...currentRecordings];
        updatedRecordings[recordingIndex] = {
          ...updatedRecordings[recordingIndex],
          src: value,
        };
        setValue("file.video_drains.recordings", updatedRecordings);
      }
    }
  };

  const handleStreamSourceSelectionChange = streamSourceIndex => e => {
    const isChecked = e.target.checked;

    let newSelectedIndices;
    let newCamOpIndex = camOpSourceIndex;

    if (isChecked) {
      newSelectedIndices = [...selectedStreamSources, streamSourceIndex].sort((a, b) => a - b);

      // Check if the source being selected has label "PAN" in stored form data
      const storedData = sourceFormData[streamSourceIndex] || {};
      if (storedData.label === "PAN") {
        newCamOpIndex = streamSourceIndex;
        setCamOpSourceIndex(streamSourceIndex);
        setExpandedAccordions(prev => ({ ...prev, [streamSourceIndex]: true }));
      }
    } else {
      // Cache the full current form source so that if the user re-selects this source,
      // their previous edits (url, parameters, exposure settings, etc.) are restored.
      const currentSelectedIndex = selectedStreamSources.indexOf(streamSourceIndex);
      if (currentSelectedIndex !== -1) {
        const currentFormSource = values.file?.stream_sources?.[currentSelectedIndex];
        if (currentFormSource) {
          setSourceFormData(prev => ({
            ...prev,
            [streamSourceIndex]: { ...(prev[streamSourceIndex] || {}), ...currentFormSource },
          }));
        }
      }

      newSelectedIndices = selectedStreamSources.filter(idx => idx !== streamSourceIndex);
      if (camOpSourceIndex === streamSourceIndex) {
        newCamOpIndex = null;
        setCamOpSourceIndex(null);
      }
      setSelectedRecordings(prev => prev.filter(idx => idx !== streamSourceIndex));
    }

    setSelectedStreamSources(newSelectedIndices);

    const currentFormSources = values.file?.stream_sources || [];
    const selectedSources = newSelectedIndices.map(idx => {
      const { description, ...rest } = allStreamSources[idx];
      const existingIndex = selectedStreamSources.indexOf(idx);
      const existingFormSource = existingIndex !== -1 ? currentFormSources[existingIndex] : {};
      const storedFormData = sourceFormData[idx] || {};

      let sourceData = {
        ...rest,
        ...existingFormSource,
        ...storedFormData,
        camOp: idx === newCamOpIndex,
      };

      // If this source has label "PAN" and is being set as Cam Op, add steerable config
      if (idx === newCamOpIndex && sourceData.label === "PAN" && !sourceData.steerable) {
        sourceData.steerable = {
          //exp_camera_url: "",
          method: "mechanical",
          auto: true,
          cfg: "",
          params: "-m",
        };
      } else if (idx !== newCamOpIndex && sourceData.steerable) {
        // Remove steerable from sources that are not Cam Op
        delete sourceData.steerable;
      }

      if (rest.brand === "Axis") {
        const currentUrl = sourceData.url || rest.url;
        const { baseUrl, params } = parseUrlParameters(currentUrl);

        const hasAudioParam = params.some(p => p.key === "audio");
        if (!hasAudioParam) {
          const newParams = [...params, { key: "audio", value: "0" }];
          sourceData.url = buildUrlWithParameters(baseUrl, newParams);
        }
      }

      if (rest.label?.startsWith("vptz")) {
        const currentUrl = sourceData.url || rest.url;
        const { baseUrl, params } = parseUrlParameters(currentUrl);

        const hasDewarpParam = params.some(p => p.key === "dewarp_id");
        if (!hasDewarpParam) {
          const newParams = [...params, { key: "dewarp_id", value: "" }];
          sourceData.url = buildUrlWithParameters(baseUrl, newParams);
        } else {
          sourceData.url = currentUrl;
        }
      }

      return sourceData;
    });
    setValue("file.stream_sources", selectedSources);

    const currentRecordings = values.file?.video_drains?.recordings || [];

    // Build updated recordings: preserve existing recordings for all selected sources
    const selectedLabels = selectedSources.map(src => src.label).filter(Boolean);

    // Keep existing recordings that match selected source labels
    const preservedRecordings = currentRecordings.filter(
      rec => rec.src !== "program" && selectedLabels.includes(rec.src),
    );

    // Add recordings for sources that are in selectedRecordings but don't have a recording yet
    const updatedRecordings = newSelectedIndices
      .filter(idx => selectedRecordings.includes(idx))
      .map(idx => {
        const newSourceIndex = newSelectedIndices.indexOf(idx);
        const newSource = selectedSources[newSourceIndex];
        const label = newSource?.label || "";

        // Check if we already have this recording preserved
        const existingRecording = preservedRecordings.find(rec => rec.src === label);
        if (existingRecording) {
          return null; // Already preserved, don't duplicate
        }

        const newRecording = { src: label };
        if (label === "PAN") {
          newRecording.chunk_type = "fmp4";
        }
        return newRecording;
      })
      .filter(Boolean); // Remove nulls

    // Combine preserved and new recordings
    const allRecordings = [...preservedRecordings, ...updatedRecordings];

    const programRecording = {
      src: "program",
    };

    setValue("file.video_drains.recordings", [...allRecordings, programRecording]);

    const currentVideoArray = values.file?.mixer?.video || [];
    // Keep only non-empty labels that are still selected; drop stale empty-string entries
    const updatedVideoArray = currentVideoArray.filter(label => label && selectedLabels.includes(label));
    setValue("file.mixer.video", updatedVideoArray);

    const currentPreselected = values.file?.mixer?.preselected || [];
    const updatedPreselected = currentPreselected.filter(label => label && selectedLabels.includes(label));
    setValue("file.mixer.preselected", updatedPreselected);
  };

  const handleCamOpChange = streamSourceIndex => e => {
    e.stopPropagation();
    const isChecked = e.target.checked;

    if (!selectedStreamSources.includes(streamSourceIndex)) {
      return;
    }

    const newCamOpIndex = isChecked ? streamSourceIndex : null;
    const selectedIndex = selectedStreamSources.indexOf(streamSourceIndex);
    const currentFormSources = values.file?.stream_sources || [];
    const togglingSource = currentFormSources[selectedIndex] || {};
    const oldLabel = togglingSource.label;

    // When turning Cam Op OFF on a source whose label is still "PAN",
    // fall back to the deployment source's original label so the source isn't left named "PAN".
    const fallbackLabel = allStreamSources[streamSourceIndex]?.label || "";
    const newLabel = isChecked ? "PAN" : oldLabel === "PAN" ? fallbackLabel : oldLabel;

    setCamOpSourceIndex(newCamOpIndex);

    if (newCamOpIndex !== null) {
      setExpandedAccordions(prev => ({ ...prev, [streamSourceIndex]: true }));
    }

    const selectedSources = selectedStreamSources.map((idx, formIndex) => {
      const existingFormSource = currentFormSources[formIndex] || {};
      const { description, ...rest } = allStreamSources[idx];

      const source = {
        ...rest,
        ...existingFormSource,
        camOp: idx === newCamOpIndex,
      };

      if (idx === streamSourceIndex) {
        source.label = newLabel;
      }

      if (idx === newCamOpIndex) {
        source.steerable = {
          method: "mechanical",
          auto: true,
          cfg: "",
          params: "-m",
        };
      } else if (source.steerable) {
        delete source.steerable;
      }

      return source;
    });
    setValue("file.stream_sources", selectedSources);

    // Rename the toggled source's label in all dependent places (mixer.video/audio/preselected, recordings)
    if (oldLabel && oldLabel !== newLabel) {
      const currentVideoArray = values.file?.mixer?.video || [];
      if (currentVideoArray.includes(oldLabel)) {
        setValue(
          "file.mixer.video",
          currentVideoArray.map(label => (label === oldLabel ? newLabel : label)),
        );
      }

      const currentAudioArray = values.file?.mixer?.audio || [];
      if (currentAudioArray.includes(oldLabel)) {
        setValue(
          "file.mixer.audio",
          currentAudioArray.map(label => (label === oldLabel ? newLabel : label)),
        );
      }

      const currentPreselected = values.file?.mixer?.preselected || [];
      if (currentPreselected.includes(oldLabel)) {
        setValue(
          "file.mixer.preselected",
          currentPreselected.map(label => (label === oldLabel ? newLabel : label)),
        );
      }

      const currentRecordings = values.file?.video_drains?.recordings || [];
      const recIndex = currentRecordings.findIndex(rec => rec.src === oldLabel);
      if (recIndex !== -1) {
        const updated = [...currentRecordings];
        updated[recIndex] = { ...updated[recIndex], src: newLabel };
        setValue("file.video_drains.recordings", updated);
      }
    }

    if (newCamOpIndex !== null) {
      // Cam Op sources cannot be preselected
      const currentPreselected = values.file?.mixer?.preselected || [];
      const updatedPreselected = currentPreselected.filter(label => label !== "PAN");
      setValue("file.mixer.preselected", updatedPreselected);
    }
  };

  const handleSteerableFieldChange = (streamSourceIndex, fieldName) => e => {
    const { value } = e.target;
    const selectedIndex = selectedStreamSources.indexOf(streamSourceIndex);
    if (selectedIndex === -1) return;

    const currentFormSources = values.file?.stream_sources || [];
    const updatedFormSources = [...currentFormSources];

    setSourceFormData(prev => ({
      ...prev,
      [streamSourceIndex]: {
        ...prev[streamSourceIndex],
        steerable: {
          ...(prev[streamSourceIndex]?.steerable || {}),
          [fieldName]: value,
        },
      },
    }));

    updatedFormSources[selectedIndex] = {
      ...updatedFormSources[selectedIndex],
      steerable: {
        ...updatedFormSources[selectedIndex].steerable,
        [fieldName]: value,
      },
    };
    setValue("file.stream_sources", updatedFormSources);
  };

  const handleUseAutoExposureChange = streamSourceIndex => e => {
    e.stopPropagation();
    const isChecked = e.target.checked;
    const selectedIndex = selectedStreamSources.indexOf(streamSourceIndex);
    if (selectedIndex === -1) return;

    const currentFormSources = values.file?.stream_sources || [];
    const updatedFormSources = [...currentFormSources];
    const currentSource = updatedFormSources[selectedIndex] || {};

    if (isChecked) {
      updatedFormSources[selectedIndex] = {
        ...currentSource,
        useAutoExposure: true,
        exp_camera_url: currentSource.exp_camera_url || "",
        exp_camera_cfg: currentSource.exp_camera_cfg || "",
      };
    } else {
      const { exp_camera_url, exp_camera_cfg, useAutoExposure, ...rest } = currentSource;
      updatedFormSources[selectedIndex] = {
        ...rest,
        useAutoExposure: false,
      };
    }

    setSourceFormData(prev => ({
      ...prev,
      [streamSourceIndex]: {
        ...prev[streamSourceIndex],
        useAutoExposure: isChecked,
        ...(isChecked ? {} : { exp_camera_url: undefined, exp_camera_cfg: undefined }),
      },
    }));

    setValue("file.stream_sources", updatedFormSources);
  };

  const handleAutoExposureFieldChange = (streamSourceIndex, fieldName) => e => {
    const { value } = e.target;
    const selectedIndex = selectedStreamSources.indexOf(streamSourceIndex);
    if (selectedIndex === -1) return;

    const currentFormSources = values.file?.stream_sources || [];
    const updatedFormSources = [...currentFormSources];

    setSourceFormData(prev => ({
      ...prev,
      [streamSourceIndex]: {
        ...prev[streamSourceIndex],
        [fieldName]: value,
      },
    }));

    updatedFormSources[selectedIndex] = {
      ...updatedFormSources[selectedIndex],
      [fieldName]: value,
    };
    setValue("file.stream_sources", updatedFormSources);
  };

  const handleOverlayFieldChange = (streamSourceIndex, fieldName) => e => {
    const { value } = e.target;
    const selectedIndex = selectedStreamSources.indexOf(streamSourceIndex);
    if (selectedIndex === -1) return;

    const currentFormSources = values.file?.stream_sources || [];
    const updatedFormSources = [...currentFormSources];
    const numValue = parseFloat(value);

    setSourceFormData(prev => ({
      ...prev,
      [streamSourceIndex]: {
        ...prev[streamSourceIndex],
        [fieldName]: isNaN(numValue) ? undefined : numValue,
      },
    }));

    updatedFormSources[selectedIndex] = {
      ...updatedFormSources[selectedIndex],
      [fieldName]: isNaN(numValue) ? undefined : numValue,
    };
    setValue("file.stream_sources", updatedFormSources);
  };

  const handleMixerVideoChange = streamSourceIndex => e => {
    e.stopPropagation();
    const isChecked = e.target.checked;
    const selectedIndex = selectedStreamSources.indexOf(streamSourceIndex);
    if (selectedIndex === -1) return;

    const currentFormSources = values.file?.stream_sources || [];
    const formSource = currentFormSources[selectedIndex];
    const label = formSource?.label;

    if (!label) return;

    const streamSource = allStreamSources[streamSourceIndex];
    const isAudioSource = streamSource?.isAudioSource || false;

    if (isAudioSource) {
      const currentAudioArray = values.file?.mixer?.audio || [];

      if (isChecked) {
        if (!currentAudioArray.includes(label)) {
          setValue("file.mixer.audio", [...currentAudioArray, label]);
        }
      } else {
        setValue("file.mixer.audio", currentAudioArray.filter(l => l !== label));
      }
    } else {
      const currentVideoArray = values.file?.mixer?.video || [];

      if (isChecked) {
        if (!currentVideoArray.includes(label)) {
          setValue("file.mixer.video", [...currentVideoArray, label]);
        }
      } else {
        setValue("file.mixer.video", currentVideoArray.filter(l => l !== label));

        const currentPreselected = values.file?.mixer?.preselected || [];
        if (currentPreselected.includes(label)) {
          setValue("file.mixer.preselected", currentPreselected.filter(l => l !== label));
        }
      }
    }
  };

  const handlePreselectedChange = streamSourceIndex => e => {
    e.stopPropagation();
    const isChecked = e.target.checked;
    const selectedIndex = selectedStreamSources.indexOf(streamSourceIndex);
    if (selectedIndex === -1) return;

    const currentFormSources = values.file?.stream_sources || [];
    const formSource = currentFormSources[selectedIndex];
    const label = formSource?.label;
    const type = formSource?.type || "auto";

    if (!label) return;

    const currentPreselected = values.file?.mixer?.preselected || [];
    let newPreselected;

    if (isChecked) {
      if (type === "auto") {
        const autoLabelsToRemove = currentFormSources
          .filter(src => {
            const srcLabel = src?.label;
            const srcType = src?.type || "auto";
            return srcType === "auto" && srcLabel && currentPreselected.includes(srcLabel);
          })
          .map(src => src.label);

        newPreselected = currentPreselected.filter(l => !autoLabelsToRemove.includes(l));
        newPreselected.push(label);
      } else {
        if (!currentPreselected.includes(label)) {
          newPreselected = [...currentPreselected, label];
        } else {
          newPreselected = currentPreselected;
        }
      }
    } else {
      newPreselected = currentPreselected.filter(l => l !== label);
    }

    setValue("file.mixer.preselected", newPreselected);
  };

  const handleAddAudioSource = streamSourceIndex => () => {
    if (audioSourceLinks.has(streamSourceIndex)) return;

    const streamSource = allStreamSources[streamSourceIndex];
    const selectedIndex = selectedStreamSources.indexOf(streamSourceIndex);
    const currentFormSources = values.file?.stream_sources || [];
    const formSource = selectedIndex !== -1 ? currentFormSources[selectedIndex] : null;

    const parentUrl = formSource?.url || streamSource.url;
    const { baseUrl: parentBaseUrl, params: parentParams } = parseUrlParameters(parentUrl);
    const parentAudioParamIndex = parentParams.findIndex(p => p.key === "audio");
    const newParentParams = [...parentParams];

    if (parentAudioParamIndex !== -1) {
      newParentParams[parentAudioParamIndex] = { key: "audio", value: "0" };
    } else {
      newParentParams.push({ key: "audio", value: "0" });
    }

    const newParentUrl = buildUrlWithParameters(parentBaseUrl, newParentParams);

    const { baseUrl: audioBaseUrl, params: audioParams } = parseUrlParameters(parentUrl);
    const audioAudioParamIndex = audioParams.findIndex(p => p.key === "audio");
    const newAudioParams = [...audioParams];

    if (audioAudioParamIndex !== -1) {
      newAudioParams[audioAudioParamIndex] = { key: "audio", value: "1" };
    } else {
      newAudioParams.push({ key: "audio", value: "1" });
    }

    const audioUrl = buildUrlWithParameters(audioBaseUrl, newAudioParams);

    const audioSource = {
      description: `${streamSource.description || ""} (Audio)`,
      cam_id: streamSource.cam_id,
      brand: streamSource.brand,
      url: audioUrl,
      label: "AUDIO",
      isAudioSource: true,
      parentIndex: streamSourceIndex,
    };

    const audioIndex = streamSourceIndex + 1;

    flushSync(() => {
      setAllStreamSources(prev => {
        const newSources = [...prev];
        newSources.splice(audioIndex, 0, audioSource);
        return newSources;
      });

      setSelectedStreamSources(prev => {
        const updated = prev.map(idx => (idx >= audioIndex ? idx + 1 : idx));
        updated.push(audioIndex);
        return updated.sort((a, b) => a - b);
      });

      setAudioSourceLinks(prev => {
        const newMap = new Map();
        for (const [parent, audio] of prev.entries()) {
          const newParent = parent >= audioIndex ? parent + 1 : parent;
          const newAudio = audio >= audioIndex ? audio + 1 : audio;
          newMap.set(newParent, newAudio);
        }
        const newParentIndex = streamSourceIndex >= audioIndex ? streamSourceIndex + 1 : streamSourceIndex;
        newMap.set(newParentIndex, audioIndex);
        return newMap;
      });
    });

    const updatedFormSources = [...currentFormSources];

    if (selectedIndex !== -1) {
      updatedFormSources[selectedIndex] = {
        ...updatedFormSources[selectedIndex],
        url: newParentUrl,
      };
      const { description, isAudioSource, parentIndex, ...audioFormSource } = audioSource;
      updatedFormSources.splice(selectedIndex + 1, 0, audioFormSource);
    } else {
      const { description, isAudioSource, parentIndex, ...audioFormSource } = audioSource;
      updatedFormSources.push(audioFormSource);
    }

    setValue("file.stream_sources", updatedFormSources);
  };

  const handleRemoveAudioSource = audioSourceIndex => () => {
    let parentIndex = null;
    for (const [parent, audio] of audioSourceLinks.entries()) {
      if (audio === audioSourceIndex) {
        parentIndex = parent;
        break;
      }
    }

    if (parentIndex === null) return;

    const parentSelectedIndex = selectedStreamSources.indexOf(parentIndex);
    if (parentSelectedIndex === -1) return;

    const currentFormSources = values.file?.stream_sources || [];
    const parentFormSource = currentFormSources[parentSelectedIndex];

    const streamSource = allStreamSources[parentIndex];
    const parentUrl = parentFormSource?.url || streamSource.url;
    const { baseUrl, params } = parseUrlParameters(parentUrl);
    const audioParamIndex = params.findIndex(p => p.key === "audio");
    const newParams = [...params];

    if (audioParamIndex !== -1) {
      newParams.splice(audioParamIndex, 1);
    }

    const newParentUrl = buildUrlWithParameters(baseUrl, newParams);

    const updatedFormSources = [...currentFormSources];
    updatedFormSources[parentSelectedIndex] = {
      ...updatedFormSources[parentSelectedIndex],
      url: newParentUrl,
    };

    const audioSelectedIndex = selectedStreamSources.indexOf(audioSourceIndex);
    if (audioSelectedIndex !== -1) {
      updatedFormSources.splice(audioSelectedIndex, 1);
    }

    setValue("file.stream_sources", updatedFormSources);

    flushSync(() => {
      setAllStreamSources(prev => prev.filter((_, idx) => idx !== audioSourceIndex));

      setSelectedStreamSources(prev =>
        prev.filter(idx => idx !== audioSourceIndex).map(idx => (idx > audioSourceIndex ? idx - 1 : idx)),
      );

      setAudioSourceLinks(prev => {
        const newMap = new Map();
        for (const [parent, audio] of prev.entries()) {
          if (audio === audioSourceIndex) continue; // Skip the removed link
          const newParent = parent > audioSourceIndex ? parent - 1 : parent;
          const newAudio = audio > audioSourceIndex ? audio - 1 : audio;
          newMap.set(newParent, newAudio);
        }
        return newMap;
      });
    });

    const currentAudioArray = values.file?.mixer?.audio || [];
    setValue("file.mixer.audio", currentAudioArray.filter(label => label !== "AUDIO"));
  };

  const handleRecordingFieldChange = (streamSourceIndex, fieldName) => e => {
    const { value } = e.target;
    if (!selectedRecordings.includes(streamSourceIndex)) return;

    const selectedIndex = selectedStreamSources.indexOf(streamSourceIndex);
    const currentFormSources = values.file?.stream_sources || [];
    const formSource = currentFormSources[selectedIndex];
    const streamSource = allStreamSources[streamSourceIndex];
    const label = formSource?.label || streamSource?.label || "";
    if (!label) return;

    const currentRecordings = values.file?.video_drains?.recordings || [];
    const recordingIndex = currentRecordings.findIndex(rec => rec.src === label);
    if (recordingIndex === -1) return;

    const updatedRecordings = [...currentRecordings];
    updatedRecordings[recordingIndex] = {
      ...updatedRecordings[recordingIndex],
      [fieldName]: value,
    };
    setValue("file.video_drains.recordings", updatedRecordings);
  };

  const handleRecordingAutorunChange = streamSourceIndex => e => {
    const isChecked = e.target.checked;
    if (!selectedRecordings.includes(streamSourceIndex)) return;

    const selectedIndex = selectedStreamSources.indexOf(streamSourceIndex);
    const currentFormSources = values.file?.stream_sources || [];
    const formSource = currentFormSources[selectedIndex];
    const streamSource = allStreamSources[streamSourceIndex];
    const label = formSource?.label || streamSource?.label || "";
    if (!label) return;

    const currentRecordings = values.file?.video_drains?.recordings || [];
    const recordingIndex = currentRecordings.findIndex(rec => rec.src === label);
    if (recordingIndex === -1) return;

    const updatedRecordings = [...currentRecordings];
    updatedRecordings[recordingIndex] = {
      ...updatedRecordings[recordingIndex],
      autorun: isChecked,
    };
    setValue("file.video_drains.recordings", updatedRecordings);
  };

  const handleProgramRecordingFieldChange = fieldName => e => {
    const { value } = e.target;
    const currentRecordings = values.file?.video_drains?.recordings || [];
    const programRecordingIndex = currentRecordings.findIndex(rec => rec.src === "program");

    if (programRecordingIndex !== -1) {
      const updatedRecordings = [...currentRecordings];
      updatedRecordings[programRecordingIndex] = {
        ...updatedRecordings[programRecordingIndex],
        [fieldName]: value,
      };
      setValue("file.video_drains.recordings", updatedRecordings);
    }
  };

  const handleProgramRecordingAutorunChange = e => {
    const isChecked = e.target.checked;
    const currentRecordings = values.file?.video_drains?.recordings || [];
    const programRecordingIndex = currentRecordings.findIndex(rec => rec.src === "program");

    if (programRecordingIndex !== -1) {
      const updatedRecordings = [...currentRecordings];
      updatedRecordings[programRecordingIndex] = {
        ...updatedRecordings[programRecordingIndex],
        autorun: isChecked,
      };
      setValue("file.video_drains.recordings", updatedRecordings);
    }
  };

  const handleProgramRecordingSelectionChange = e => {
    const isChecked = e.target.checked;
    const currentRecordings = values.file?.video_drains?.recordings || [];
    const programRecordingIndex = currentRecordings.findIndex(rec => rec.src === "program");

    if (isChecked) {
      if (programRecordingIndex === -1) {
        const newProgramRecording = {
          src: "program",
        };
        setValue("file.video_drains.recordings", [...currentRecordings, newProgramRecording]);
      }
    } else {
      if (programRecordingIndex !== -1) {
        const updatedRecordings = currentRecordings.filter((_, idx) => idx !== programRecordingIndex);
        setValue("file.video_drains.recordings", updatedRecordings);
      }
    }
  };

  const handleRecordingSelectionChange = streamSourceIndex => e => {
    const isChecked = e.target.checked;

    const selectedIndex = selectedStreamSources.indexOf(streamSourceIndex);
    const currentFormSources = values.file?.stream_sources || [];
    const formSource = currentFormSources[selectedIndex];
    const streamSource = allStreamSources[streamSourceIndex];
    const label = formSource?.label || streamSource?.label || "";

    if (!label) return;

    let newSelectedRecordings;
    if (isChecked) {
      newSelectedRecordings = [...selectedRecordings, streamSourceIndex].sort((a, b) => a - b);
    } else {
      newSelectedRecordings = selectedRecordings.filter(idx => idx !== streamSourceIndex);
    }

    setSelectedRecordings(newSelectedRecordings);

    const currentRecordings = values.file?.video_drains?.recordings || [];

    const updatedRecordings = newSelectedRecordings
      .map(idx => {
        const selectedIdx = selectedStreamSources.indexOf(idx);
        if (selectedIdx === -1) return null;

        const fSource = currentFormSources[selectedIdx];
        const sSource = allStreamSources[idx];
        const srcLabel = fSource?.label || sSource?.label || "";

        const existingRecording = currentRecordings.find(rec => rec.src === srcLabel);

        if (existingRecording) {
          return existingRecording;
        }

        const newRecording = { src: srcLabel };
        if (srcLabel === "PAN") {
          newRecording.chunk_type = "fmp4";
        }
        return newRecording;
      })
      .filter(Boolean);

    const existingProgramRecording = currentRecordings.find(rec => rec.src === "program");
    const programRecording = existingProgramRecording || { src: "program" };
    setValue("file.video_drains.recordings", [...updatedRecordings, programRecording]);
  };

  const handleFileChange = e => {
    const { files } = e.currentTarget;
    if (files && files.length > 0) {
      const file = files[0];
      setUploadedFileName(file.name);
      const reader = new FileReader();
      reader.onload = event => {
        try {
          const jsonContent = JSON.parse(event.target.result);
          onFileUploaded?.(file);
          if (Array.isArray(jsonContent?.stream_sources)) {
            jsonContent.stream_sources = jsonContent.stream_sources.map(src => {
              // vptz sources never have exposure fields; don't flip the UI flag for them.
              if (src?.label?.startsWith("vptz")) {
                return src;
              }
              const hasExposureData =
                (!!src?.exp_camera_url && String(src.exp_camera_url).trim() !== "") ||
                (!!src?.exp_camera_cfg && String(src.exp_camera_cfg).trim() !== "");
              return hasExposureData ? { ...src, useAutoExposure: true } : src;
            });
          }
          if (jsonContent?.mixer?.score?.drawn && jsonContent?.mixer?.score?.burnt) {
            jsonContent.mixer.score.include_secondary = true;
          }
          // Merge with defaults so top-level required fields are never lost if the uploaded
          // JSON omitted them (shallow merge: uploaded values win on keys they define).
          const mergedFile = { ...(defaultValues.file || {}), ...jsonContent };
          setValue("file", mergedFile);
        } catch (error) {
          setUploadedFileName("");
        }
      };
      reader.readAsText(file);
    }
  };

  if (renderBasicFieldsOnly) {
    return (
      <>
        <DataGridItem>
          <StyledTextField
            type="text"
            id="codename"
            name="codename"
            value={values.codename || ""}
            onChange={handleChange}
            label={intl.formatMessage({ id: "common.filename" })}
            error={!!errors.codename}
            helperText={errors.codename?.message}
          />
        </DataGridItem>
        <DataGridItem>
          <StyledTextField
            type="text"
            id="description"
            name="description"
            value={values.description || ""}
            onChange={handleChange}
            label={intl.formatMessage({ id: "common.description" })}
            error={!!errors.description}
            helperText={errors.description?.message}
          />
        </DataGridItem>
        <DataGridItem>
          <SimpleCheckbox
            id="test"
            name="test"
            value="test"
            checked={values.test || false}
            onChange={handleChange}
            label={intl.formatMessage({ id: "roomTemplate.test" })}
          />
        </DataGridItem>
        <DataGridItem big>
          <Typography variant="h6" gutterBottom className={classes.inputModeTitle}>
            {intl.formatMessage({ id: "roomTemplate.inputMode.title" }, { defaultMessage: "Configuration Method" })}
          </Typography>
          <Typography variant="body2" color="textSecondary" className={classes.inputModeDescription}>
            {intl.formatMessage(
              { id: "roomTemplate.inputMode.description" },
              { defaultMessage: "Choose how you want to configure the room template" },
            )}
          </Typography>
          <div className={classes.inputModeButtons}>
            <input
              type="file"
              id="file-upload-input"
              name="file"
              accept="application/json"
              onChange={handleFileChange}
              className={classes.hiddenFileInput}
            />
            <label htmlFor="file-upload-input">
              <Button
                variant={inputMode === "upload" ? "contained" : "outlined"}
                color="primary"
                startIcon={<CloudUploadIcon />}
                component="span"
                onClick={() => setInputMode("upload")}
              >
                {intl.formatMessage({ id: "roomTemplate.inputMode.upload" }, { defaultMessage: "Upload JSON File" })}
              </Button>
            </label>
            <Button
              variant={inputMode === "form" || inputMode === "formHidden" ? "contained" : "outlined"}
              color="primary"
              onClick={() => {
                if (inputMode === "form") {
                  setInputMode("formHidden");
                } else {
                  setInputMode("form");
                  if (!values.file?.name) {
                    setValue("file.name", "");
                  }
                  if (!values.file?.set_dns_names) {
                    setValue("file.set_dns_names", [""]);
                  }
                }
              }}
            >
              {intl.formatMessage(
                { id: inputMode === "form" ? "roomTemplate.inputMode.hideForm" : "roomTemplate.inputMode.form" },
                { defaultMessage: inputMode === "form" ? "Hide Form" : "Fill Out Form" },
              )}
            </Button>
          </div>
          {inputMode === "upload" && uploadedFileName && (
            <Typography variant="body2" color="primary" className={classes.fileUploadedText}>
              ✓ {intl.formatMessage({ id: "roomTemplate.fileUploaded" }, { defaultMessage: "File uploaded:" })}{" "}
              <strong className={classes.fileUploadedName}>{uploadedFileName}</strong>
            </Typography>
          )}
          {errors.file && (
            <Typography variant="caption" color="error" className={classes.uploadError}>
              {errors.file?.message}
            </Typography>
          )}
        </DataGridItem>
      </>
    );
  }

  if (renderSection) {
    if (inputMode !== "form") return null;

    switch (renderSection) {
      case "fileConfiguration":
        return <FileConfigurationSection values={values} errors={errors} setValue={setValue} />;

      case "virtualCam":
        return (
          <VirtualCamSection
            values={values}
            setValue={setValue}
            errors={errors}
            allStreamSources={allStreamSources}
            setAllStreamSources={setAllStreamSources}
            selectedStreamSources={selectedStreamSources}
            setSelectedStreamSources={setSelectedStreamSources}
            handleAddVptzSource={handleAddVptzSource}
          />
        );

      case "streamSources":
        return (
          <StreamSourcesSection
            allStreamSources={allStreamSources}
            values={values}
            errors={errors}
            selectedStreamSources={selectedStreamSources}
            setSelectedStreamSources={setSelectedStreamSources}
            camOpSourceIndex={camOpSourceIndex}
            expandedAccordions={expandedAccordions}
            setExpandedAccordions={setExpandedAccordions}
            handleStreamSourceSelectionChange={handleStreamSourceSelectionChange}
            handleStreamSourceLabelChange={handleStreamSourceLabelChange}
            handleCamOpChange={handleCamOpChange}
            handleAddAudioSource={handleAddAudioSource}
            handleRemoveAudioSource={handleRemoveAudioSource}
            audioSourceLinks={audioSourceLinks}
            handlePreselectedChange={handlePreselectedChange}
            handleMixerVideoChange={handleMixerVideoChange}
            handleStreamSourceTypeChange={handleStreamSourceTypeChange}
            handleOverlayFieldChange={handleOverlayFieldChange}
            handleSteerableFieldChange={handleSteerableFieldChange}
            handleAutoExposureFieldChange={handleAutoExposureFieldChange}
            handleUseAutoExposureChange={handleUseAutoExposureChange}
            handleAddUrlParameter={handleAddUrlParameter}
            getUrlParameters={getUrlParameters}
            handleUrlParameterChange={handleUrlParameterChange}
            handleRemoveUrlParameter={handleRemoveUrlParameter}
            setAllStreamSources={setAllStreamSources}
            setValue={setValue}
            sourceFormData={sourceFormData}
          />
        );

      case "recordings":
        return (
          <RecordingsSection
            values={values}
            allStreamSources={allStreamSources}
            selectedStreamSources={selectedStreamSources}
            selectedRecordings={selectedRecordings}
            handleRecordingSelectionChange={handleRecordingSelectionChange}
            handleRecordingAutorunChange={handleRecordingAutorunChange}
            handleRecordingFieldChange={handleRecordingFieldChange}
            handleProgramRecordingSelectionChange={handleProgramRecordingSelectionChange}
            handleProgramRecordingAutorunChange={handleProgramRecordingAutorunChange}
            handleProgramRecordingFieldChange={handleProgramRecordingFieldChange}
          />
        );

      case "mixer":
        return <MixerSection values={values} setValue={setValue} />;

      case "scoreboard":
        return (
          <ScoreboardSection values={values} setValue={setValue} allStreamSources={allStreamSources} errors={errors} />
        );

      case "advancedSettings":
        return <AdvancedSettingsSection values={values} setValue={setValue} />;

      default:
        return null;
    }
  }

  return null;
};

NewRoomTemplateForm.propTypes = {
  /**
   * Objeto intl de react-intl
   */
  intl: PropTypes.object.isRequired,
  /**
   * Valores del formulario
   */
  values: PropTypes.object,
  /**
   * Función setValue de react-hook-form
   */
  setValue: PropTypes.func,
  /**
   * Errores de validación de react-hook-form
   */
  errors: PropTypes.object,
  /**
   * Clases de estilos
   */
  classes: PropTypes.object,
  /**
   * Errores de validación del servidor
   */
  validationErrors: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  /**
   * Callback para saltar la validación
   */
  handleSkipValidation: PropTypes.func,
  /**
   * Todas las fuentes de stream disponibles (incluye description para visualización)
   */
  allStreamSources: PropTypes.arrayOf(PropTypes.object),
  /**
   * Función para actualizar las fuentes de stream disponibles
   */
  setAllStreamSources: PropTypes.func,
  /**
   * Renderizar solo los campos básicos y selector de modo
   */
  renderBasicFieldsOnly: PropTypes.bool,
  /**
   * Sección específica a renderizar
   */
  renderSection: PropTypes.string,
  /**
   * Modo de entrada actual (upload o form)
   */
  inputMode: PropTypes.string,
  /**
   * Función para cambiar el modo de entrada
   */
  setInputMode: PropTypes.func,
  /**
   * Callback que recibe el File original cuando el usuario sube un JSON
   */
  onFileUploaded: PropTypes.func,
};

export default injectIntl(withStyles(styles)(NewRoomTemplateForm));
